import { sectionRenderer } from '@theme/section-renderer';
import { Component } from '@theme/component';
import { FilterUpdateEvent, ThemeEvents } from '@theme/events';
import { debounce, formatMoney, getActiveCollectionGrid } from '@theme/utilities';

/**
 * Search query parameter.
 * @type {string}
 */
const SEARCH_QUERY = 'q';

const FACET_SWATCH_ORDER_KEY = 'facet-swatch-order';

/**
 * @returns {string}
 */
function getFacetSwatchOrderStorageKey() {
  return `${FACET_SWATCH_ORDER_KEY}:${window.location.pathname}`;
}

/**
 * @param {ParentNode} root
 */
function captureFacetSwatchOrders(root = document) {
  const storageKey = getFacetSwatchOrderStorageKey();
  /** @type {Record<string, string[]>} */
  let stored = {};

  try {
    const raw = sessionStorage.getItem(storageKey);
    if (raw) stored = JSON.parse(raw);
  } catch {
    stored = {};
  }

  let updated = false;

  root.querySelectorAll('[data-facet-swatch-list]').forEach((list) => {
    if (!(list instanceof HTMLElement)) return;

    const param = list.getAttribute('data-filter-param');
    if (!param) return;

    const values = [...list.querySelectorAll(':scope > li[data-filter-value]')]
      .map((item) => item.getAttribute('data-filter-value'))
      .filter((value) => Boolean(value));

    if (!values.length) return;

    const existing = stored[param];
    if (!existing?.length) {
      stored[param] = values;
      updated = true;
      return;
    }

    const merged = [...existing];
    values.forEach((value) => {
      if (!merged.includes(value)) merged.push(value);
    });

    if (merged.length !== existing.length) {
      stored[param] = merged;
      updated = true;
    }
  });

  if (updated || !sessionStorage.getItem(storageKey)) {
    sessionStorage.setItem(storageKey, JSON.stringify(stored));
  }
}

/**
 * @param {ParentNode} root
 */
function restoreFacetSwatchOrders(root = document) {
  const storageKey = getFacetSwatchOrderStorageKey();
  /** @type {Record<string, string[]>} */
  let stored = {};

  try {
    const raw = sessionStorage.getItem(storageKey);
    if (raw) stored = JSON.parse(raw);
  } catch {
    return;
  }

  if (!Object.keys(stored).length) return;

  root.querySelectorAll('[data-facet-swatch-list]').forEach((list) => {
    if (!(list instanceof HTMLElement)) return;

    const param = list.getAttribute('data-filter-param');
    const order = param ? stored[param] : null;
    if (!order?.length) return;

    /** @type {Map<string, HTMLLIElement>} */
    const itemsByValue = new Map();

    list.querySelectorAll(':scope > li[data-filter-value]').forEach((item) => {
      if (!(item instanceof HTMLLIElement)) return;

      const value = item.getAttribute('data-filter-value');
      if (value) itemsByValue.set(value, item);
    });

    order.forEach((value) => {
      const item = itemsByValue.get(value);
      if (item) list.appendChild(item);
    });

    /* Re-apply column count after reorder */
    const count = list.querySelectorAll(':scope > li[data-filter-value]').length;
    if (count > 0) {
      let columns = Math.min(count, 4);
      const mod4 = count % 4;
      const mod3 = count % 3;
      if (count > 4 && mod4 !== 0 && mod4 !== 3 && (mod3 === 0 || mod3 === 2)) {
        columns = 3;
      }
      list.style.setProperty('--swatch-columns', String(columns));
    }
  });
}

function clearFacetSwatchOrders() {
  sessionStorage.removeItem(getFacetSwatchOrderStorageKey());
}

/**
 * @param {ParentNode} root
 */
function syncFacetSwatchOrders(root = document) {
  captureFacetSwatchOrders(root);
  restoreFacetSwatchOrders(root);
}

function initFacetsLayoutAfterRender(root = document) {
  syncFacetSwatchOrders(root);
}

/** @type {(root?: ParentNode) => void} */
const syncFacetSwatchOrdersDebounced = debounce(syncFacetSwatchOrders, 40);

document.addEventListener(ThemeEvents.FilterUpdate, (event) => {
  if (!(event instanceof FilterUpdateEvent)) return;

  if (!event.shouldShowClearAll()) {
    clearFacetSwatchOrders();
  }
});

/**
 * Handles the main facets form functionality
 *
 * @typedef {Object} FacetsFormRefs
 * @property {HTMLFormElement} facetsForm - The main facets form element
 * @property {HTMLElement | undefined} facetStatus - The facet status element
 *
 * @extends {Component<FacetsFormRefs>}
 */
class FacetsFormComponent extends Component {
  requiredRefs = ['facetsForm'];
  
  #buttonObserver = null;
  
  connectedCallback() {
    super.connectedCallback();
    // Watch for apply button being added to DOM
    this.#watchForApplyButton();
    syncFacetSwatchOrders(document.getElementById(this.sectionId) ?? this);
  }
  
  disconnectedCallback() {
    super.disconnectedCallback();
    // Clean up observer
    if (this.#buttonObserver) {
      this.#buttonObserver.disconnect();
      this.#buttonObserver = null;
    }
  }
  
  /**
   * Watches for apply button being added to DOM and ensures it's visible
   */
  #watchForApplyButton() {
    if (!window.MutationObserver) return;
    
    // First, check if button already exists and make it visible
    this.#ensureExistingButtonVisible();
    
    this.#buttonObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) { // Element node
            // Check if the added node is the apply button or contains it
            const applyButton = node.matches?.('button[data-apply-button="true"].facets__apply-filters')
              ? node
              : node.querySelector?.('button[data-apply-button="true"].facets__apply-filters');
            
            if (applyButton instanceof HTMLButtonElement) {
              // Ensure button is visible immediately
              this.#makeButtonVisible(applyButton);
            }
          }
        });
        
        // Also check attributes changes (in case button visibility is toggled)
        if (mutation.type === 'attributes' && mutation.attributeName === 'style') {
          const target = mutation.target;
          if (target instanceof HTMLButtonElement && target.hasAttribute('data-apply-button')) {
            this.#makeButtonVisible(target);
          }
        }
      });
    });
    
    // Observe the section for changes
    const observeSection = () => {
      const sectionElement = document.getElementById(this.sectionId);
      if (sectionElement) {
        this.#buttonObserver.observe(sectionElement, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['style', 'class']
        });
      } else {
        // Retry if section not found yet
        setTimeout(observeSection, 100);
      }
    };
    
    observeSection();
  }
  
  /**
   * Makes a button visible with all necessary styles
   */
  #makeButtonVisible(button) {
    if (!(button instanceof HTMLButtonElement)) return;
    
    // Remove any inline display:none
    button.style.removeProperty('display');
    button.style.removeProperty('visibility');
    button.style.removeProperty('opacity');
    
    // Force visibility
    button.style.setProperty('display', 'grid', 'important');
    button.style.setProperty('visibility', 'visible', 'important');
    button.style.setProperty('opacity', '1', 'important');
    button.style.setProperty('position', 'relative', 'important');
    
    // Remove loading state
    button.classList.remove('loading');
    button.disabled = false;
    
    // Ensure parent containers are visible
    let parent = button.parentElement;
    while (parent && parent !== document.body) {
      if (parent.style.display === 'none') {
        parent.style.setProperty('display', '', 'important');
      }
      parent = parent.parentElement;
    }
  }
  
  /**
   * Ensures existing apply button is visible
   */
  #ensureExistingButtonVisible() {
    const sectionElement = document.getElementById(this.sectionId);
    if (sectionElement) {
      // Try multiple selectors
      const applyButton = sectionElement.querySelector('button[data-apply-button="true"].facets__apply-filters') ||
                         sectionElement.querySelector('button.facets__apply-filters[data-apply-button="true"]') ||
                         sectionElement.querySelector('.facets__apply-filters[data-apply-button="true"]') ||
                         sectionElement.querySelector('button[data-apply-button="true"]');
      
      if (applyButton instanceof HTMLButtonElement) {
        this.#makeButtonVisible(applyButton);
      } else {
        // Search entire document as fallback
        const allApplyButtons = document.querySelectorAll('button[data-apply-button="true"]');
        allApplyButtons.forEach(btn => {
          if (btn instanceof HTMLButtonElement) {
            this.#makeButtonVisible(btn);
          }
        });
      }
    }
  }

  /**
   * Creates URL parameters from form data
   * @param {FormData} [formData] - Optional form data to use instead of the main form
   * @returns {URLSearchParams} The processed URL parameters
   */
  createURLParameters(formData = new FormData(this.refs.facetsForm)) {
    // Ensure we have a valid form reference
    if (!this.refs.facetsForm) {
      return new URLSearchParams();
    }

    let newParameters = new URLSearchParams(/** @type any */ (formData));

    // Clean up empty price filter values
    if (newParameters.get('filter.v.price.gte') === '') newParameters.delete('filter.v.price.gte');
    if (newParameters.get('filter.v.price.lte') === '') newParameters.delete('filter.v.price.lte');

    // Remove page parameter to reset pagination
    newParameters.delete('page');

    // Preserve search query if present
    const searchQuery = this.#getSearchQuery();
    if (searchQuery) newParameters.set(SEARCH_QUERY, searchQuery);

    return newParameters;
  }

  /**
   * Gets the search query parameter from the current URL
   * @returns {string} The search query
   */
  #getSearchQuery() {
    const url = new URL(window.location.href);
    return url.searchParams.get(SEARCH_QUERY) ?? '';
  }

  get sectionId() {
    const id = this.getAttribute('section-id');
    if (!id) throw new Error('Section ID is required');
    return id;
  }

  /**
   * Updates the URL hash with current filter parameters
   */
  #updateURLHash() {
    const url = new URL(window.location.href);
    const urlParameters = this.createURLParameters();

    url.search = '';
    for (const [param, value] of urlParameters.entries()) {
      if (value) {
        url.searchParams.append(param, value);
      }
    }

    history.pushState({ urlParameters: urlParameters.toString() }, '', url.toString());
  }

  /**
   * Updates filters and renders the section - optimized for speed
   */
  updateFilters = () => {
    // Update URL first (synchronous)
    this.#updateURLHash();
    
    // Get URL parameters for the event
    const urlParameters = this.createURLParameters();
    
    // Dispatch filter update event
    this.dispatchEvent(new FilterUpdateEvent(urlParameters));
    
    // Update section immediately (no delay needed - URL is already updated)
    this.#updateSection();
  };

  /**
   * Updates the section - optimized for maximum speed with error handling
   */
  #updateSection() {
    const viewTransition = !this.closest('dialog');
    const resultsList = document.querySelector('results-list');
    
    this.#showLoadingState();
    
    // Instant scroll to top when filters are applied/cleared (blink-of-eye speed)
    if (resultsList && viewTransition) {
      const resultsElement = document.getElementById('ResultsList');
      if (resultsElement) {
        resultsElement.scrollIntoView({ behavior: 'auto', block: 'start' });
      }
    }

    // Render section without cache for filter updates (filters change URL, cache won't help)
    // This ensures we always get fresh results
    const renderSection = async () => {
      try {
        return await sectionRenderer.renderSection(this.sectionId, { cache: false });
      } catch (error) {
        // Enhance error with more context
        if (error instanceof TypeError && error.message.includes('fetch')) {
          error.status = 0; // Network error
        }
        throw error;
      }
    };
    
    let renderPromise;
    // Skip view transition for all filter updates (apply + clear) so products appear immediately
    renderPromise = renderSection();
    
    // Set timeout for request (20 seconds - reduced from 30 for better UX)
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => {
        const timeoutError = new Error('Request timeout');
        timeoutError.name = 'TimeoutError';
        reject(timeoutError);
      }, 20000);
    });
    
    // Race between render and timeout
    Promise.race([renderPromise, timeoutPromise])
      .then(() => {
        // Hide loading state
        this.#hideLoadingState();
        // Ensure apply button is visible after section re-render
        this.#ensureApplyButtonAfterRender();
        // Remount pz cards + re-wire infinite scroll after morph (UI unchanged)
        this.#afterFilterSectionRender();
      })
      .catch((error) => {
        // Hide loading state
        this.#hideLoadingState();
        
        // Enhance error object with status if available
        if (!error.status && error.message) {
          // Try to extract status from error message if it's a fetch error
          if (error.message.includes('404')) {
            error.status = 404;
          } else if (error.message.includes('500')) {
            error.status = 500;
          }
        }
        
        // Show user-friendly error message
        this.#showErrorMessage(error);
        // Always ensure button state is reset even on error
        this.#ensureApplyButtonAfterRender();
      });
  }

  /**
   * After section morph: refresh collection cards in-place and notify the grid.
   * Scoped to the collection section so other page cards are untouched.
   * Does not change markup, filters, or card architecture — only re-inits JS state
   * on nodes Morph reused without firing connectedCallback.
   */
  #afterFilterSectionRender() {
    const section =
      document.getElementById(`shopify-section-${this.sectionId}`) ||
      document.getElementById(this.sectionId);
    const resultsList =
      section?.querySelector('results-list') || document.querySelector('results-list');
    const root = resultsList || section || document;

    document.dispatchEvent(
      new CustomEvent('pz-collection-cards:mount', {
        detail: { root, reason: 'filter' },
      })
    );

    document.dispatchEvent(
      new CustomEvent('collection:filters:rendered', {
        bubbles: true,
        detail: { sectionId: this.sectionId, root },
      })
    );
  }

  /**
   * Shows minimal loading state (no overlay/skeletons) so section morph is instant
   */
  #showLoadingState() {
    const resultsList = document.querySelector('results-list');
    const grid = (resultsList && getActiveCollectionGrid(resultsList)) || document.querySelector('.product-grid');
    if (!grid) return;
    grid.classList.add('loading');
    grid.setAttribute('aria-busy', 'true');
    grid.setAttribute('aria-label', 'Loading products');
    // No overlay, no skeletons – section morph replaces content immediately
  }

  /**
   * Hides loading state on product grid
   * Optimized for faster removal with smooth transitions
   */
  #hideLoadingState() {
    const resultsList = document.querySelector('results-list');
    const grid = (resultsList && getActiveCollectionGrid(resultsList)) || document.querySelector('.product-grid');
    if (!grid) return;
    grid.classList.remove('loading', 'clearing-filters');
    grid.removeAttribute('aria-busy');
    grid.removeAttribute('aria-label');
  }

  /**
   * Detects the type of error and returns user-friendly message
   * @param {Error} error - The error object
   * @returns {Object} Object with message and retryable flag
   */
  #getErrorMessage(error) {
    // Network errors
    if (error.name === 'TypeError' && error.message.includes('fetch')) {
      return {
        message: 'Network connection issue. Please check your internet connection and try again.',
        retryable: true,
        type: 'network'
      };
    }
    
    // Timeout errors
    if (error.message === 'Request timeout' || error.name === 'TimeoutError') {
      return {
        message: 'The request took too long. The server may be busy. Please try again.',
        retryable: true,
        type: 'timeout'
      };
    }
    
    // Abort errors (user-initiated, shouldn't show error)
    if (error.name === 'AbortError') {
      return {
        message: null, // Don't show error for aborted requests
        retryable: false,
        type: 'abort'
      };
    }
    
    // Server errors (5xx)
    if (error.status >= 500) {
      return {
        message: 'Server error occurred. Please try again in a moment.',
        retryable: true,
        type: 'server'
      };
    }
    
    // Client errors (4xx)
    if (error.status >= 400 && error.status < 500) {
      return {
        message: 'Unable to process your request. Please refresh the page.',
        retryable: true,
        type: 'client'
      };
    }
    
    // Generic errors
    return {
      message: 'Unable to load products. Please try again.',
      retryable: true,
      type: 'generic'
    };
  }

  /**
   * Shows user-friendly error message with retry option
   */
  #showErrorMessage(error) {
    const resultsList = document.querySelector('results-list');
    if (!resultsList) return;
    
    // Get error details
    const errorDetails = this.#getErrorMessage(error);
    
    // Don't show error for aborted requests
    if (errorDetails.type === 'abort') {
      return;
    }
    
    // Remove any existing error messages
    const existingError = resultsList.querySelector('.collection-error-message');
    if (existingError) {
      existingError.remove();
    }
    
    // Create error message element
    const errorMessage = document.createElement('div');
    errorMessage.className = 'collection-error-message';
    errorMessage.setAttribute('role', 'alert');
    errorMessage.setAttribute('aria-live', 'polite');

    const content = document.createElement('div');
    content.className = 'error-message__content';

    const icon = document.createElement('div');
    icon.className = 'error-message__icon';
    icon.setAttribute('aria-hidden', 'true');
    icon.innerHTML = `
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M10 6V10M10 14H10.01M19 10C19 14.9706 14.9706 19 10 19C5.02944 19 1 14.9706 1 10C1 5.02944 5.02944 1 10 1C14.9706 1 19 5.02944 19 10Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>`;

    const textWrapper = document.createElement('div');
    textWrapper.className = 'error-message__text-wrapper';
    const text = document.createElement('p');
    text.className = 'error-message__text';
    text.textContent = errorDetails.message || '';
    textWrapper.appendChild(text);

    content.appendChild(icon);
    content.appendChild(textWrapper);

    if (errorDetails.retryable) {
      const retryBtn = document.createElement('button');
      retryBtn.type = 'button';
      retryBtn.className = 'button error-message__retry';
      retryBtn.setAttribute('aria-label', 'Retry loading products');
      retryBtn.textContent = 'Try Again';
      retryBtn.addEventListener('click', () => {
        errorMessage.remove();
        this.updateFilters();
      });
      content.appendChild(retryBtn);
    }

    errorMessage.appendChild(content);
    
    // Insert error message before product grid
    const grid = getActiveCollectionGrid(resultsList) || resultsList.querySelector('.product-grid');
    if (grid && grid.parentElement) {
      grid.parentElement.insertBefore(errorMessage, grid);
    } else {
      resultsList.appendChild(errorMessage);
    }
    
    // Auto-remove error after 15 seconds (increased from 10)
    setTimeout(() => {
      if (errorMessage.parentElement) {
        errorMessage.remove();
      }
    }, 15000);
  }

  /**
   * Ensures apply button is visible after section re-render
   */
  #ensureApplyButtonAfterRender() {
    let attempts = 0;
    const maxAttempts = 5;
    
    const ensureButton = () => {
      attempts++;
      const sectionElement = document.getElementById(this.sectionId);
      if (sectionElement) {
        // Try multiple selectors to find the apply button - search in entire section
        const applyButton = sectionElement.querySelector('button[data-apply-button="true"].facets__apply-filters') ||
                          sectionElement.querySelector('.facets__apply-filters[data-apply-button="true"]') ||
                          sectionElement.querySelector('button.facets__apply-filters[data-apply-button="true"]') ||
                          sectionElement.querySelector('.facets__actions button[data-apply-button="true"]') ||
                          sectionElement.querySelector('.facets__actions .facets__apply-filters') ||
                          sectionElement.querySelector('.facets__apply-filters') ||
                          sectionElement.querySelector('button.facets__apply-filters');
        
        syncFacetSwatchOrdersDebounced(sectionElement);

        if (applyButton instanceof HTMLButtonElement) {
          // Force visibility with inline styles (for vertical filters) - highest priority
          // Remove any display:none that might be set
          applyButton.style.removeProperty('display');
          applyButton.style.setProperty('display', 'grid', 'important');
          applyButton.style.setProperty('visibility', 'visible', 'important');
          applyButton.style.setProperty('opacity', '1', 'important');
          // Remove loading state if present
          applyButton.classList.remove('loading');
          applyButton.disabled = false;
          
          // Also ensure parent container is visible
          const actionsContainer = applyButton.closest('.facets__actions');
          if (actionsContainer) {
            actionsContainer.style.setProperty('display', '', 'important');
            actionsContainer.style.setProperty('visibility', '', 'important');
            actionsContainer.style.setProperty('opacity', '', 'important');
          }
          
          // Also check if button is inside vertical filter wrapper
          const verticalWrapper = applyButton.closest('.facets-block-wrapper--vertical');
          if (verticalWrapper) {
            // Ensure wrapper doesn't hide the button
            verticalWrapper.style.setProperty('display', '', 'important');
          }
          
          return; // Found and fixed, stop trying
        }
      }
      
      // If button not found and we haven't exceeded max attempts, try again
      if (attempts < maxAttempts) {
        setTimeout(ensureButton, 200);
      } else {
        // Last attempt failed - button not found after max attempts
        // Try one more time with a broader search
        const allButtons = document.querySelectorAll('button[data-apply-button="true"]');
        if (allButtons.length > 0) {
          // Buttons exist but not in expected section - ensure they're visible anyway
          allButtons.forEach(btn => {
            if (btn instanceof HTMLButtonElement) {
              this.#makeButtonVisible(btn);
            }
          });
        }
      }
    };
    
    // Start trying immediately
    requestAnimationFrame(ensureButton);
    // Also try after morph typically completes at different stages
    setTimeout(ensureButton, 50);
    setTimeout(ensureButton, 100);
    setTimeout(ensureButton, 200);
    setTimeout(ensureButton, 300);
    setTimeout(ensureButton, 500);
    setTimeout(ensureButton, 800);
  }

  /**
   * Updates filters based on a provided URL
   * @param {string} url - The URL to update filters with
   */
  updateFiltersByURL(url) {
    history.pushState('', '', url);
    this.dispatchEvent(new FilterUpdateEvent(this.createURLParameters()));
    this.#updateSection();
  }

  /**
   * Applies filters when the apply button is clicked
   * @param {Event} event - The click event
   */
  applyFilters(event) {
    if (event instanceof Event) {
      event.preventDefault();
      
      // Add loading state to apply button
      const applyButton = event.target.closest('.facets__apply-filters');
      if (applyButton instanceof HTMLButtonElement) {
        applyButton.classList.add('loading');
        applyButton.disabled = true;
      }
    }
    
    // Update price filters if they have values but haven't been applied
    const priceFacets = this.querySelectorAll('price-facet-component');
    priceFacets.forEach((priceFacet) => {
      if (priceFacet instanceof PriceFacetComponent) {
        const { minInput, maxInput } = priceFacet.refs;
        if (minInput && maxInput && (minInput.value || maxInput.value)) {
          // Validate and adjust price inputs before applying
          priceFacet.updatePriceFilterAndResults();
        }
      }
    });
    
    // Apply filters - this will trigger section re-render
    this.updateFilters();

    // Mobile filter drawer: close after Apply so shoppers see updated results
    if (window.innerWidth < 750) {
      const drawer =
        this.closest('dialog-component#filters-drawer') ||
        document.getElementById('filters-drawer');
      if (drawer && typeof drawer.closeDialog === 'function') {
        drawer.closeDialog();
      } else if (drawer instanceof HTMLElement) {
        const dialog = drawer.querySelector('dialog');
        if (dialog instanceof HTMLDialogElement && dialog.open) dialog.close();
      }
    }
    
    // Loading state will be removed in #ensureApplyButtonAfterRender after section renders
    // No need to duplicate the render call here
  }
}

if (!customElements.get('facets-form-component')) {
  customElements.define('facets-form-component', FacetsFormComponent);
}

/**
 * @typedef {Object} FacetInputsRefs
 * @property {HTMLInputElement[]} facetInputs - The facet input elements
 */

/**
 * Handles individual facet input functionality
 * @extends {Component<FacetInputsRefs>}
 */
class FacetInputsComponent extends Component {
  get sectionId() {
    const id = this.closest('.shopify-section')?.id;
    if (!id) throw new Error('FacetInputs component must be a child of a section');
    return id;
  }

  /**
   * Updates filters and the selected facet summary
   */
  updateFilters() {
    const facetsForm = this.closest('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;

    // Check if this is a manual apply scenario (vertical filters)
    const isManualApply = this.hasAttribute('data-manual-apply');
    
    if (isManualApply) {
      // For manual apply, just update the summary but don't apply filters
      // User needs to click Apply button
      this.#updateSelectedFacetSummary();
      // Ensure apply button is visible
      this.#ensureApplyButtonVisible();
    } else {
      // Auto-apply for horizontal filters
      facetsForm.updateFilters();
      this.#updateSelectedFacetSummary();
    }
  }

  /**
   * Ensures the apply button is visible for vertical filters
   */
  #ensureApplyButtonVisible() {
    const applyButton = this.closest('facets-form-component')?.querySelector('.facets__apply-filters');
    if (applyButton instanceof HTMLButtonElement) {
      applyButton.style.display = '';
      applyButton.style.visibility = '';
      applyButton.style.opacity = '';
    }
  }

  /**
   * Handles keydown events for the facets form
   * @param {KeyboardEvent} event - The keydown event
   */
  handleKeyDown(event) {
    if (!(event.target instanceof HTMLElement)) return;
    const closestInput = event.target.querySelector('input');

    if (!(closestInput instanceof HTMLInputElement)) return;

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      closestInput.checked = !closestInput.checked;
      this.updateFilters();
    }
  }

  /**
   * Handles mouseover events on facet labels
   * @param {MouseEvent} event - The mouseover event
   */
  prefetchPage = debounce((event) => {
    if (!(event.target instanceof HTMLElement)) return;

    const form = this.closest('form');
    if (!form) return;

    const formData = new FormData(form);
    const inputElement = event.target.querySelector('input');

    if (!(inputElement instanceof HTMLInputElement)) return;

    if (!inputElement.checked) formData.append(inputElement.name, inputElement.value);

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    const urlParameters = facetsForm.createURLParameters(formData);

    const url = new URL(window.location.pathname, window.location.origin);

    for (const [key, value] of urlParameters) url.searchParams.append(key, value);

    if (inputElement.checked) url.searchParams.delete(inputElement.name, inputElement.value);

    sectionRenderer.getSectionHTML(this.sectionId, true, url);
  }, 200);

  cancelPrefetchPage = () => this.prefetchPage.cancel();

  /**
   * Updates the selected facet summary
   */
  #updateSelectedFacetSummary() {
    if (!this.refs.facetInputs) return;

    const checkedInputElements = this.refs.facetInputs.filter((input) => input.checked);
    const details = this.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent.updateListSummary(checkedInputElements);
  }
}

if (!customElements.get('facet-inputs-component')) {
  customElements.define('facet-inputs-component', FacetInputsComponent);
}

/**
 * @typedef {Object} PriceFacetRefs
 * @property {HTMLInputElement} minInput - The minimum price input
 * @property {HTMLInputElement} maxInput - The maximum price input
 */

/**
 * Handles price facet functionality
 * @extends {Component<PriceFacetRefs>}
 */
class PriceFacetComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keydown', this.#onKeyDown);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keydown', this.#onKeyDown);
  }

  /**
   * Handles keydown events to restrict input to valid characters
   * @param {KeyboardEvent} event - The keydown event
   */
  #onKeyDown = (event) => {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  };

  /**
   * Updates price filter and results
   */
  updatePriceFilterAndResults() {
    const { minInput, maxInput } = this.refs;

    this.#adjustToValidValues(minInput);
    this.#adjustToValidValues(maxInput);

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    // Check if this is a manual apply scenario (vertical filters)
    const isManualApply = this.hasAttribute('data-manual-apply');
    
    if (isManualApply) {
      // For manual apply, just update the summary but don't apply filters
      // User needs to click Apply button
      this.#setMinAndMaxValues();
      this.#updateSummary();
      // Ensure apply button is visible
      this.#ensureApplyButtonVisible();
    } else {
      // Auto-apply for horizontal filters
      facetsForm.updateFilters();
      this.#setMinAndMaxValues();
      this.#updateSummary();
    }
  }

  /**
   * Ensures the apply button is visible for vertical filters
   */
  #ensureApplyButtonVisible() {
    const facetsForm = this.closest('facets-form-component');
    if (facetsForm) {
      const applyButton = facetsForm.querySelector('.facets__apply-filters');
      if (applyButton instanceof HTMLButtonElement) {
        applyButton.style.display = '';
        applyButton.style.visibility = '';
        applyButton.style.opacity = '';
      }
    }
  }

  /**
   * Adjusts input values to be within valid range
   * @param {HTMLInputElement} input - The input element to adjust
   */
  #adjustToValidValues(input) {
    if (input.value.trim() === '') return;

    const value = Number(input.value);
    const min = Number(formatMoney(input.getAttribute('data-min') ?? ''));
    const max = Number(formatMoney(input.getAttribute('data-max') ?? ''));

    if (value < min) input.value = min.toString();
    if (value > max) input.value = max.toString();
  }

  /**
   * Sets min and max values for the inputs
   */
  #setMinAndMaxValues() {
    const { minInput, maxInput } = this.refs;

    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', '0');
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max') ?? '');
  }

  /**
   * Updates the price summary
   */
  #updateSummary() {
    const { minInput, maxInput } = this.refs;
    const details = this.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent?.updatePriceSummary(minInput, maxInput);
  }
}

if (!customElements.get('price-facet-component')) {
  customElements.define('price-facet-component', PriceFacetComponent);
}

/**
 * Handles clearing of facet filters
 * @extends {Component}
 */
class FacetClearComponent extends Component {
  requiredRefs = ['clearButton'];

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keyup', this.#handleKeyUp);
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  /**
   * Clears the filter
   * @param {Event} event - The click event
   */
  clearFilter(event) {
    if (!(event.target instanceof HTMLElement)) return;

    if (event instanceof KeyboardEvent) {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }
      event.preventDefault();
    }

    const container = event.target.closest('facet-inputs-component, price-facet-component');
    container?.querySelectorAll('[type="checkbox"]:checked, input').forEach((input) => {
      if (input instanceof HTMLInputElement) {
        input.checked = false;
        input.value = '';
      }
    });

    const details = event.target.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent.clearSummary();

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
  }

  /**
   * Handles keyup events
   * @param {KeyboardEvent} event - The keyup event
   */
  #handleKeyUp = (event) => {
    if (event.metaKey) return;
    if (event.key === 'Enter') this.clearFilter(event);
  };

  /**
   * Toggle clear button visibility when filters are applied. Happens before the
   * Section Rendering Request resolves.
   *
   * @param {FilterUpdateEvent} event
   */
  #handleFilterUpdate = (event) => {
    const { clearButton } = this.refs;
    if (clearButton instanceof Element) {
      clearButton.classList.toggle('facets__clear--active', event.shouldShowClearAll());
    }
  };
}

if (!customElements.get('facet-clear-component')) {
  customElements.define('facet-clear-component', FacetClearComponent);
}

/**
 * @typedef {Object} FacetRemoveComponentRefs
 * @property {HTMLInputElement | undefined} clearButton - The button to clear filters
 */

/**
 * Handles removal of individual facet filters
 * @extends {Component<FacetRemoveComponentRefs>}
 */
class FacetRemoveComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  /**
   * Removes the filter
   * @param {Object} data - The data object
   * @param {string} data.form - The form to remove the filter from
   * @param {Event} event - The click event
   */
  removeFilter({ form }, event) {
    if (event instanceof KeyboardEvent) {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }
      event.preventDefault();
    }

    const url = this.dataset.url;
    if (!url) return;

    const facetsForm = form ? document.getElementById(form) : this.closest('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;

    // Remove buttons always auto-apply (no need for Apply button)
    facetsForm.updateFiltersByURL(url);
  }

  /**
   * Toggle clear button visibility when filters are applied. Happens before the
   * Section Rendering Request resolves.
   *
   * @param {FilterUpdateEvent} event
   */
  #handleFilterUpdate = (event) => {
    const { clearButton } = this.refs;
    if (clearButton instanceof Element) {
      clearButton.classList.toggle('active', event.shouldShowClearAll());
    }
  };
}

if (!customElements.get('facet-remove-component')) {
  customElements.define('facet-remove-component', FacetRemoveComponent);
}

/**
 * Handles sorting filter functionality
 *
 * @typedef {Object} SortingFilterRefs
 *
 * @extends {Component}
 */
class SortingFilterComponent extends Component {
  /**
   * Updates filter and sorting with loading state
   * @param {Event} event - The change event
   */
  updateFilterAndSorting(event) {
    const facetsForm =
      this.closest('facets-form-component') || this.closest('.shopify-section')?.querySelector('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;
    const isMobile = window.innerWidth < 750;

    const shouldDisable = this.dataset.shouldUseSelectOnMobile === 'true';

    // Bind the chosen sort control to the live facets form when it sits outside
    // the form markup (vertical drawer toolbar). Without this, FormData omits sort_by.
    const formEl = facetsForm.refs?.facetsForm;
    const changed = event?.target;
    if (
      formEl instanceof HTMLFormElement &&
      formEl.id &&
      (changed instanceof HTMLInputElement || changed instanceof HTMLSelectElement) &&
      changed.name === 'sort_by'
    ) {
      if (changed.getAttribute('form') !== formEl.id) {
        changed.setAttribute('form', formEl.id);
      }
      // Keep sibling sort controls in the same form group in sync
      const selector = `input[name="sort_by"][form="${formEl.id}"], select[name="sort_by"][form="${formEl.id}"]`;
      document.querySelectorAll(selector).forEach((control) => {
        if (control === changed) return;
        if (control instanceof HTMLInputElement && control.type === 'radio') {
          control.checked = control.value === changed.value;
        } else if (control instanceof HTMLSelectElement) {
          control.value = changed.value;
        }
      });
    }

    // Because we have a select element on mobile and a bunch of radio buttons on desktop,
    // we need to disable the input during "form-submission" to prevent duplicate entries.
    if (shouldDisable) {
      if (isMobile) {
        const inputs = this.querySelectorAll('input[name="sort_by"]');
        inputs.forEach((input) => {
          if (!(input instanceof HTMLInputElement)) return;
          input.disabled = true;
        });
      } else {
        const selectElement = this.querySelector('select[name="sort_by"]');
        if (!(selectElement instanceof HTMLSelectElement)) return;
        selectElement.disabled = true;
      }
    }

    // Update filters (which will show loading state)
    facetsForm.updateFilters();
    this.updateFacetStatus(event);

    // Mobile filter/sort drawer: close after a sort choice is applied
    if (isMobile) {
      const drawer =
        this.closest('dialog-component#filters-drawer') ||
        document.getElementById('filters-drawer');
      if (drawer && typeof drawer.closeDialog === 'function') {
        drawer.closeDialog();
      } else if (drawer instanceof HTMLElement) {
        const dialog = drawer.querySelector('dialog');
        if (dialog instanceof HTMLDialogElement && dialog.open) dialog.close();
      }
    }

    // Re-enable the input after the form-submission
    if (shouldDisable) {
      if (isMobile) {
        const inputs = this.querySelectorAll('input[name="sort_by"]');
        inputs.forEach((input) => {
          if (!(input instanceof HTMLInputElement)) return;
          input.disabled = false;
        });
      } else {
        const selectElement = this.querySelector('select[name="sort_by"]');
        if (!(selectElement instanceof HTMLSelectElement)) return;
        selectElement.disabled = false;
      }
    }

    // Close the details element when a value is selected
    const { details } = this.refs;
    if (!(details instanceof HTMLDetailsElement)) return;
    details.open = false;
  }

  /**
   * Updates the facet status
   * @param {Event} event - The change event
   */
  updateFacetStatus(event) {
    if (!(event.target instanceof HTMLSelectElement)) return;

    const details = this.querySelector('details');
    if (!details) return;

    const facetStatus = details.querySelector('facet-status-component');
    if (!(facetStatus instanceof FacetStatusComponent)) return;

    facetStatus.textContent =
      event.target.value !== details.dataset.defaultSortBy ? event.target.dataset.optionName ?? '' : '';
  }
}

if (!customElements.get('sorting-filter-component')) {
  customElements.define('sorting-filter-component', SortingFilterComponent);
}

/**
 * @typedef {Object} FacetStatusRefs
 * @property {HTMLElement} facetStatus - The facet status element
 */

/**
 * Handles facet status display
 * @extends {Component<FacetStatusRefs>}
 */
class FacetStatusComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
  }

  /**
   * Updates the list summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   */
  updateListSummary(checkedInputElements) {
    const checkedInputElementsCount = checkedInputElements.length;

    this.getAttribute('facet-type') === 'swatches'
      ? this.#updateSwatchSummary(checkedInputElements, checkedInputElementsCount)
      : this.#updateBubbleSummary(checkedInputElements, checkedInputElementsCount);
  }

  /**
   * Updates the swatch summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   * @param {number} checkedInputElementsCount - The number of checked inputs
   */
  #updateSwatchSummary(checkedInputElements, checkedInputElementsCount) {
    const { facetStatus } = this.refs;
    facetStatus.classList.remove('bubble', 'facets__bubble');

    if (checkedInputElementsCount === 0) {
      facetStatus.replaceChildren();
      return;
    }

    if (checkedInputElementsCount > 3) {
      facetStatus.textContent = checkedInputElementsCount.toString();
      facetStatus.classList.add('bubble', 'facets__bubble');
      return;
    }

    facetStatus.replaceChildren();
    for (const inputElement of checkedInputElements) {
      const swatch = inputElement.parentElement?.querySelector('span.swatch');
      if (swatch) facetStatus.appendChild(swatch.cloneNode(true));
    }
  }

  /**
   * Updates the bubble summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   * @param {number} checkedInputElementsCount - The number of checked inputs
   */
  #updateBubbleSummary(checkedInputElements, checkedInputElementsCount) {
    const { facetStatus } = this.refs;
    facetStatus.classList.remove('bubble', 'facets__bubble');

    if (checkedInputElementsCount === 0) {
      facetStatus.replaceChildren();
      return;
    }

    if (checkedInputElementsCount === 1) {
      // textContent avoids interpreting catalog labels as HTML
      facetStatus.textContent = checkedInputElements[0]?.dataset.label ?? '';
      return;
    }

    facetStatus.textContent = checkedInputElementsCount.toString();
    facetStatus.classList.add('bubble', 'facets__bubble');
  }

  /**
   * Updates the price summary
   * @param {HTMLInputElement} minInput - The minimum price input
   * @param {HTMLInputElement} maxInput - The maximum price input
   */
  updatePriceSummary(minInput, maxInput) {
    const minInputValue = minInput.value;
    const maxInputValue = maxInput.value;
    const { facetStatus } = this.refs;

    if (!minInputValue && !maxInputValue) {
      facetStatus.replaceChildren();
      return;
    }

    const minInputNum = this.#parseCents(minInputValue, '0');
    const maxInputNum = this.#parseCents(maxInputValue, facetStatus.dataset.rangeMax);
    // Formatted money is plain text ({{amount}} placeholders); never inject as HTML
    facetStatus.textContent = `${this.#formatMoney(minInputNum)}–${this.#formatMoney(maxInputNum)}`;
  }

  /**
   * Parses a decimal number as cents
   * @param {string} value - The stringified decimal number to parse
   * @param {string} fallback - The fallback value in case `value` is invalid
   * @returns {number} The money value in cents
   */
  #parseCents(value, fallback = '0') {
    const parts = value ? value.trim().split(/[^0-9]/) : (parseInt(fallback, 10) / 100).toString();
    const [wholeStr, fractionStr, ...rest] = parts;
    if (typeof wholeStr !== 'string' || rest.length > 0) return parseInt(fallback, 10);

    const whole = parseInt(wholeStr, 10);
    let fraction = parseInt(fractionStr || '0', 10);

    // Use two most-significant digits, e.g. 1 -> 10, 12 -> 12, 123 -> 12.3, 1234 -> 12.34, etc
    fraction = fraction * Math.pow(10, 2 - fraction.toString().length);

    return whole * 100 + fraction;
  }

  /**
   * Formats money, replicated the implementation of the `money` liquid filters
   * @param {number} moneyValue - The money value
   * @returns {string} The formatted money value
   */
  #formatMoney(moneyValue) {
    if (!(this.refs.moneyFormat instanceof HTMLTemplateElement)) return '';

    const template = this.refs.moneyFormat.content.textContent || '{{amount}}';
    const currency = this.refs.facetStatus.dataset.currency || '';

    return template.replace(/{{\s*(\w+)\s*}}/g, (_, placeholder) => {
      if (typeof placeholder !== 'string') return '';
      if (placeholder === 'currency') return currency;

      let thousandsSeparator = ',';
      let decimalSeparator = '.';
      let precision = CURRENCY_DECIMALS[currency.toUpperCase()] ?? DEFAULT_CURRENCY_DECIMALS;

      if (placeholder === 'amount') {
        // Check first since it's the most common, use defaults.
      } else if (placeholder === 'amount_no_decimals') {
        precision = 0;
      } else if (placeholder === 'amount_with_comma_separator') {
        thousandsSeparator = '.';
        decimalSeparator = ',';
      } else if (placeholder === 'amount_no_decimals_with_comma_separator') {
        // Weirdly, this is correct. It uses amount_with_comma_separator's
        // behaviour but removes decimals, resulting in an unintuitive
        // output that can't possibly include commas, despite the name.
        thousandsSeparator = '.';
        precision = 0;
      } else if (placeholder === 'amount_no_decimals_with_space_separator') {
        thousandsSeparator = ' ';
        precision = 0;
      } else if (placeholder === 'amount_with_space_separator') {
        thousandsSeparator = ' ';
        decimalSeparator = ',';
      } else if (placeholder === 'amount_with_period_and_space_separator') {
        thousandsSeparator = ' ';
        decimalSeparator = '.';
      } else if (placeholder === 'amount_with_apostrophe_separator') {
        thousandsSeparator = "'";
        decimalSeparator = '.';
      }

      return this.#formatCents(moneyValue, thousandsSeparator, decimalSeparator, precision);
    });
  }

  /**
   * Formats money in cents
   * @param {number} moneyValue - The money value in cents (hundredths of one major currency unit)
   * @param {string} thousandsSeparator - The thousands separator
   * @param {string} decimalSeparator - The decimal separator
   * @param {number} precision - The precision
   * @returns {string} The formatted money value
   */
  #formatCents(moneyValue, thousandsSeparator, decimalSeparator, precision) {
    const roundedNumber = (moneyValue / 100).toFixed(precision);

    let [a, b] = roundedNumber.split('.');
    if (!a) a = '0';
    if (!b) b = '';

    // Split by groups of 3 digits
    a = a.replace(/\d(?=(\d\d\d)+(?!\d))/g, (digit) => digit + thousandsSeparator);

    return precision <= 0 ? a : a + decimalSeparator + b.padEnd(precision, '0');
  }

  /**
   * Clears the summary
   */
  clearSummary() {
    this.refs.facetStatus.replaceChildren();
  }
}

if (!customElements.get('facet-status-component')) {
  customElements.define('facet-status-component', FacetStatusComponent);
}

/**
 * Default currency decimals used in most currenies
 * @constant {number}
 */
const DEFAULT_CURRENCY_DECIMALS = 2;

/**
 * Decimal precision for currencies that have a non-default precision
 * @type {Record<string, number>}
 */
const CURRENCY_DECIMALS = {
  BHD: 3,
  BIF: 0,
  BYR: 0,
  CLF: 4,
  CLP: 0,
  DJF: 0,
  GNF: 0,
  IQD: 3,
  ISK: 0,
  JOD: 3,
  JPY: 0,
  KMF: 0,
  KRW: 0,
  KWD: 3,
  LYD: 3,
  MRO: 5,
  OMR: 3,
  PYG: 0,
  RWF: 0,
  TND: 3,
  UGX: 0,
  UYI: 0,
  UYW: 4,
  VND: 0,
  VUV: 0,
  XAF: 0,
  XAG: 0,
  XAU: 0,
  XBA: 0,
  XBB: 0,
  XBC: 0,
  XBD: 0,
  XDR: 0,
  XOF: 0,
  XPD: 0,
  XPF: 0,
  XPT: 0,
  XSU: 0,
  XTS: 0,
  XUA: 0,
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initFacetsLayoutAfterRender());
} else {
  initFacetsLayoutAfterRender();
}
